import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { concat, fn } from "@ember/helper";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import PluginOutlet from "discourse/components/plugin-outlet";
import MenuTab from "discourse/components/user-menu/menu-tab";
import lazyHash from "discourse/helpers/lazy-hash";
import { bind } from "discourse/lib/decorators";
import getUrl from "discourse/lib/get-url";
import { wantsNewWindow } from "discourse/lib/intercept-click";
import UserMenuTab, { CUSTOM_TABS_CLASSES } from "discourse/lib/user-menu/tab";
import { NO_REMINDER_ICON } from "discourse/models/bookmark";
import { i18n } from "discourse-i18n";
import UserMenuBookmarksList from "./bookmarks-list.js";
import UserMenuLikesNotificationsList from "./likes-notifications-list.js";
import UserMenuMentionsNotificationsList from "./mentions-notifications-list.js";
import UserMenuMessagesList from "./messages-list.js";
import UserMenuNotificationsList from "./notifications-list.js";
import UserMenuOtherNotificationsList from "./other-notifications-list.js";
import UserMenuProfileTabContent from "./profile-tab-content.gjs";
import UserMenuRepliesNotificationsList from "./replies-notifications-list.js";
import UserMenuReviewablesList from "./reviewables-list.js";

const DEFAULT_TAB_ID = "all-notifications";
const DEFAULT_PANEL_COMPONENT = UserMenuNotificationsList;

const REVIEW_QUEUE_TAB_ID = "review-queue";
const PROGRESSIVE_TAB_IDS = new Set([
  "replies",
  "mentions",
  "likes",
  "other-notifications",
]);

const CORE_TOP_TABS = [
  class extends UserMenuTab {
    id = DEFAULT_TAB_ID;
    icon = "bell";
    panelComponent = DEFAULT_PANEL_COMPONENT;

    get linkWhenActive() {
      return `${this.currentUser.path}/notifications`;
    }
  },

  class extends UserMenuTab {
    id = "replies";
    icon = "user_menu.replies";
    panelComponent = UserMenuRepliesNotificationsList;

    get notificationTypes() {
      const types = ["group_mentioned", "posted", "quoted", "replied"];
      return this.siteSettings.enable_user_menu_mentions_tab
        ? types
        : ["mentioned", ...types];
    }

    get count() {
      return this.notificationTypes.reduce(
        (sum, type) => sum + this.getUnreadCountForType(type),
        0
      );
    }

    get linkWhenActive() {
      return `${this.currentUser.path}/notifications/responses`;
    }
  },

  class extends UserMenuTab {
    id = "mentions";
    icon = "at";
    panelComponent = UserMenuMentionsNotificationsList;
    notificationTypes = ["mentioned"];

    get shouldDisplay() {
      return this.siteSettings.enable_user_menu_mentions_tab;
    }

    get count() {
      return this.getUnreadCountForType("mentioned");
    }

    get linkWhenActive() {
      return `${this.currentUser.path}/notifications/mentions`;
    }
  },

  class extends UserMenuTab {
    id = "likes";
    icon = "heart";
    panelComponent = UserMenuLikesNotificationsList;

    get shouldDisplay() {
      return !this.currentUser.user_option.likes_notifications_disabled;
    }

    get count() {
      return (
        this.getUnreadCountForType("liked") +
        this.getUnreadCountForType("liked_consolidated") +
        this.getUnreadCountForType("reaction")
      );
    }

    // TODO(osama): reaction is a type used by the reactions plugin, but it's
    // added here temporarily until we add a plugin API for extending
    // filterByTypes in lists
    get notificationTypes() {
      return ["liked", "liked_consolidated", "reaction"];
    }

    get linkWhenActive() {
      return `${this.currentUser.path}/notifications/likes-received`;
    }
  },

  class extends UserMenuTab {
    id = "messages";
    icon = "notification.private_message";
    panelComponent = UserMenuMessagesList;
    notificationTypes = ["private_message", "group_message_summary"];

    get count() {
      return this.getUnreadCountForType("private_message");
    }

    get shouldDisplay() {
      return this.currentUser?.can_send_private_messages;
    }

    get linkWhenActive() {
      return `${this.currentUser.path}/messages`;
    }
  },

  class extends UserMenuTab {
    id = "bookmarks";
    icon = NO_REMINDER_ICON;
    panelComponent = UserMenuBookmarksList;
    notificationTypes = ["bookmark_reminder"];

    get count() {
      return this.getUnreadCountForType("bookmark_reminder");
    }

    get linkWhenActive() {
      return `${this.currentUser.path}/activity/bookmarks`;
    }
  },

  class extends UserMenuTab {
    id = REVIEW_QUEUE_TAB_ID;
    icon = "flag";
    panelComponent = UserMenuReviewablesList;
    linkWhenActive = getUrl("/review");

    get shouldDisplay() {
      return (
        this.currentUser.can_review && this.currentUser.get("reviewable_count")
      );
    }

    get count() {
      return this.currentUser.get("reviewable_count");
    }
  },
];

const CORE_BOTTOM_TABS = [
  class extends UserMenuTab {
    id = "profile";
    icon = "user";
    panelComponent = UserMenuProfileTabContent;

    get linkWhenActive() {
      return `${this.currentUser.path}/summary`;
    }
  },
];

const CORE_OTHER_NOTIFICATIONS_TAB = class extends UserMenuTab {
  id = "other-notifications";
  icon = "discourse-other-tab";
  panelComponent = UserMenuOtherNotificationsList;

  constructor(currentUser, siteSettings, site, otherNotificationTypes) {
    super(...arguments);
    this.otherNotificationTypes = otherNotificationTypes;
  }

  get count() {
    return this.otherNotificationTypes.reduce((sum, notificationType) => {
      return sum + this.getUnreadCountForType(notificationType);
    }, 0);
  }

  get notificationTypes() {
    return this.otherNotificationTypes;
  }
};

export default class UserMenu extends Component {
  @service appEvents;
  @service currentUser;
  @service router;
  @service site;
  @service siteSettings;

  @tracked currentTabId = DEFAULT_TAB_ID;
  @tracked currentPanelComponent = DEFAULT_PANEL_COMPONENT;
  @tracked currentNotificationTypes;

  #availableNotificationTypes;

  constructor() {
    super(...arguments);
    // Snapshot visibility so notifications cannot move tabs while the menu is open.
    const availableTypes = this.currentUser.available_notification_types;
    this.#availableNotificationTypes = availableTypes
      ? new Set(availableTypes)
      : null;
    this.router.on("routeDidChange", this.onRouteChange);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.router.off("routeDidChange", this.onRouteChange);
  }

  get classNames() {
    let classes = ["user-menu", "revamped", "menu-panel", "drop-down"];
    if (this.siteSettings.show_user_menu_avatars) {
      classes.push("show-avatars");
    }
    return classes.join(" ");
  }

  @cached
  get topTabs() {
    const tabs = [];

    CORE_TOP_TABS.forEach((tabClass) => {
      const tab = new tabClass(this.currentUser, this.siteSettings, this.site);
      if (tab.shouldDisplay || tab.id === this.currentTabId) {
        tabs.push(tab);
      }
    });

    let reviewQueueTabIndex = tabs.findIndex(
      (tab) => tab.id === REVIEW_QUEUE_TAB_ID
    );

    CUSTOM_TABS_CLASSES.forEach((tabClass) => {
      const tab = new tabClass(this.currentUser, this.siteSettings, this.site);
      if (tab.shouldDisplay) {
        if (reviewQueueTabIndex === -1) {
          tabs.push(tab);
        } else {
          tabs.splice(reviewQueueTabIndex, 0, tab);
          reviewQueueTabIndex++;
        }
      }
    });

    tabs.push(
      new CORE_OTHER_NOTIFICATIONS_TAB(
        this.currentUser,
        this.siteSettings,
        this.site,
        this.#notificationTypesForTheOtherTab(tabs)
      )
    );

    return tabs
      .filter((tab) => this.#hasNotifications(tab))
      .map((tab, index) => {
        tab.position = index;
        return tab;
      });
  }

  @cached
  get bottomTabs() {
    const tabs = [];

    CORE_BOTTOM_TABS.forEach((tabClass) => {
      const tab = new tabClass(this.currentUser, this.siteSettings, this.site);
      if (tab.shouldDisplay) {
        tabs.push(tab);
      }
    });

    const topTabsLength = this.topTabs.length;
    return tabs.map((tab, index) => {
      tab.position = index + topTabsLength;
      return tab;
    });
  }

  @bind
  onRouteChange() {
    this.args.closeUserMenu();
  }

  @action
  handleTabClick(tab, event) {
    if (wantsNewWindow(event) || this.currentTabId === tab.id) {
      // Allow normal navigation to href
      return;
    }

    if (event.type === "keydown" && event.keyCode !== 13) {
      return;
    }

    event.preventDefault();

    this.currentTabId = tab.id;
    this.currentPanelComponent = tab.panelComponent;

    this.appEvents.trigger("user-menu:tab-click", tab.id);
    this.currentNotificationTypes = tab.notificationTypes;
  }

  @action
  triggerRenderedAppEvent() {
    this.appEvents.trigger("user-menu:rendered");
  }

  @action
  focusFirstTab(topTabsContainerElement) {
    topTabsContainerElement.querySelector(".btn.active")?.focus();
  }

  #hasNotifications(tab) {
    if (
      !PROGRESSIVE_TAB_IDS.has(tab.id) ||
      this.#availableNotificationTypes === null
    ) {
      return true;
    }

    return tab.notificationTypes.some((type) =>
      this.#availableNotificationTypes.has(this.site.notification_types[type])
    );
  }

  #notificationTypesForTheOtherTab(tabs) {
    const usedNotificationTypes = tabs
      .filter((tab) => tab.notificationTypes)
      .map((tab) => tab.notificationTypes)
      .flat();
    return Object.keys(this.site.notification_types).filter(
      (notificationType) => !usedNotificationTypes.includes(notificationType)
    );
  }

  <template>
    <div
      class={{this.classNames}}
      data-max-width="320"
      data-tab-id={{this.currentTabId}}
      {{didInsert this.triggerRenderedAppEvent}}
    >
      <div class="panel-body">
        <div class="panel-body-contents">
          <div
            aria-label={{i18n "user_menu.sr_menu_tabs"}}
            aria-orientation="vertical"
            class="menu-tabs-container"
            role="tablist"
          >
            <div class="top-tabs tabs-list" {{didInsert this.focusFirstTab}}>
              {{#each this.topTabs key="id" as |tab|}}
                <MenuTab
                  @currentTabId={{this.currentTabId}}
                  @onTabClick={{fn this.handleTabClick tab}}
                  @tab={{tab}}
                />
              {{/each}}
            </div>
            <div class="bottom-tabs tabs-list">
              {{#each this.bottomTabs key="id" as |tab|}}
                <MenuTab
                  @currentTabId={{this.currentTabId}}
                  @onTabClick={{fn this.handleTabClick tab}}
                  @tab={{tab}}
                />
              {{/each}}
            </div>

            <PluginOutlet
              @name="user-menu-tabs-list__after"
              @outletArgs={{lazyHash user=this.currentUser}}
            />
          </div>
          <div
            class="quick-access-panel"
            id={{concat "quick-access-" this.currentTabId}}
          >
            <this.currentPanelComponent
              @ariaLabelledby={{concat "user-menu-button-" this.currentTabId}}
              @class={{concat "user-menu-button-" this.currentTabId}}
              @closeUserMenu={{@closeUserMenu}}
              @filterByTypes={{this.currentNotificationTypes}}
            />
          </div>
        </div>
      </div>
    </div>
  </template>
}
